/**
 * Login — fresh magic link via POST /public/prospect-login.
 * Anti-enumeration: always shows the same confirmation message.
 */

import { useState } from 'react';
import { requestLoginLink } from '../lib/api';
import TurnstileWidget from '../components/TurnstileWidget';

interface Props {
  navigate: (path: string) => void;
}

const inputClasses =
  'w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500';

export default function Login({ navigate: _navigate }: Props) {
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!email.trim() && !!turnstileToken && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    const res = await requestLoginLink(email.trim().toLowerCase(), turnstileToken!);
    setSubmitting(false);
    if (!res.success) {
      setError(res.error || 'Something went wrong. Please try again.');
      setTurnstileToken(null);
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch { /* widget not initialized */ }
      }
      return;
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-8 text-center max-w-md mx-auto">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Check your email</h1>
        <p className="text-slate-600 dark:text-slate-300 text-sm">
          If we have a record for that email, we just sent a link.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-6 sm:p-8 max-w-md mx-auto">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Sign back in</h1>
      <p className="text-slate-600 dark:text-slate-300 mb-6">
        Already signed up? Enter your email and we'll send you a new link to continue.
      </p>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className={inputClasses}
          />
        </div>

        <TurnstileWidget
          onToken={setTurnstileToken}
          onError={() => setTurnstileToken(null)}
          onExpired={() => setTurnstileToken(null)}
        />

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full px-4 py-3 bg-blue-600 dark:bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Sending…' : 'Email me a link'}
        </button>

        <p className="text-xs text-slate-500 dark:text-slate-400 text-center pt-2">
          Haven't signed up yet?{' '}
          <a href="/" className="text-blue-600 dark:text-blue-400 hover:underline">
            Start a new signup
          </a>
        </p>
      </form>
    </div>
  );
}
